import { Camera, CheckCircle2, Loader2, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

/**
 * Live progress dialog for the "Re-snapshot" backfill job on TestDetailPage.
 *
 * Replaces the prior single-toast UX. Drives off a `job` object that's
 * mutated by the parent's polling loop (`TestDetailPage.handleResnapshot`):
 *
 *   {
 *     status: "running" | "done" | "failed",
 *     total: number,
 *     captured: number,
 *     skipped: number,
 *     failed: number,
 *     current: number,
 *     errors: [{step_index, step_id, phase, error}, ...],
 *     fatalError?: string,   // top-level job failure (e.g. browser engine unavailable)
 *   }
 *
 * The dialog cannot be dismissed while the job is running — closing
 * would abandon the polling loop. Once `status` is terminal, the
 * close button is enabled and clicking it calls `onClose`.
 */
export function ResnapshotProgressDialog({ open, job, onClose }) {
  const status = job?.status ?? "running";
  const terminal = status === "done" || status === "failed";

  // Hard-prevent ESC / overlay clicks from closing the dialog while
  // the job is still running. The user can still see the X button
  // but it's disabled — clicking it does nothing.
  const handleOpenChange = (next) => {
    if (!next && terminal) onClose?.();
  };

  const total = job?.total ?? 0;
  const captured = job?.captured ?? 0;
  const skipped = job?.skipped ?? 0;
  const failed = job?.failed ?? 0;
  const current = job?.current ?? 0;
  const errors = job?.errors ?? [];
  const fatalError = job?.fatalError;
  const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;

  let headline = "Re-snapshotting recorded steps…";
  let HeadlineIcon = Loader2;
  let headlineClass = "text-[#3B82F6] animate-spin";
  if (status === "done") {
    if (captured > 0 && failed > 0) {
      // Mixed-success branch: surface the partial failure in the
      // headline itself so users skimming don't miss it. Errors list
      // below still has the per-step detail. (Iteration 31 polish
      // from the testing agent review.)
      HeadlineIcon = CheckCircle2;
      headlineClass = "text-amber-500";
      headline = `Captured ${captured} · ${failed} step${failed === 1 ? "" : "s"} failed`;
    } else if (captured > 0) {
      HeadlineIcon = CheckCircle2;
      headlineClass = "text-emerald-500";
      headline = `Captured ${captured} baseline snapshot${captured === 1 ? "" : "s"}`;
    } else {
      // Zero-capture done state — a green checkmark is misleading
      // here because nothing was actually captured. Use the amber
      // alert affordance so the visual matches the outcome.
      HeadlineIcon = XCircle;
      headlineClass = "text-amber-500";
      headline = "No snapshots captured";
    }
  } else if (status === "failed") {
    HeadlineIcon = XCircle;
    headlineClass = "text-red-500";
    headline = "Re-snapshot job failed";
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="resnapshot-progress-dialog"
        className="max-w-md"
        // shadcn Dialog respects the open-change handler for ESC + overlay.
        // We additionally block X-button when not terminal (handled below
        // via pointer-events on the close-affordance).
        onEscapeKeyDown={(e) => { if (!terminal) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (!terminal) e.preventDefault(); }}
        onInteractOutside={(e) => { if (!terminal) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px] font-semibold text-[#0F172A]">
            <Camera size={16} className="text-[#3B82F6]" />
            Re-snapshot recorded steps
          </DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            Walking through the test in a headless browser to refresh
            baseline snapshots used for failure-diff diagnostics. You
            can leave this dialog open or close it after it finishes.
          </DialogDescription>
        </DialogHeader>

        {/* Headline status row */}
        <div className="mt-2 flex items-center gap-2 text-sm font-medium text-[#0F172A]">
          <HeadlineIcon size={16} className={headlineClass} />
          <span data-testid="resnapshot-headline">{headline}</span>
        </div>

        {/* Progress bar */}
        <div className="mt-4 space-y-1.5">
          <div className="flex items-center justify-between text-xs text-[#64748B]">
            <span data-testid="resnapshot-progress-text">
              Step {Math.min(current, total)} of {total}
            </span>
            <span>{pct}%</span>
          </div>
          <div className="h-1.5 w-full bg-[#E5E7EB] rounded-full overflow-hidden">
            <div
              data-testid="resnapshot-progress-bar"
              className={
                status === "failed"
                  ? "h-full bg-red-500 transition-all duration-300"
                  : status === "done"
                  ? "h-full bg-emerald-500 transition-all duration-300"
                  : "h-full bg-[#3B82F6] transition-all duration-300"
              }
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Counters */}
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-md border border-[#E5E7EB] bg-[#F8FAFC] py-2.5">
            <div data-testid="resnapshot-captured" className="text-lg font-semibold text-emerald-600">
              {captured}
            </div>
            <div className="text-[11px] uppercase tracking-wide text-[#64748B]">Captured</div>
          </div>
          <div className="rounded-md border border-[#E5E7EB] bg-[#F8FAFC] py-2.5">
            <div data-testid="resnapshot-skipped" className="text-lg font-semibold text-[#0F172A]">
              {skipped}
            </div>
            <div className="text-[11px] uppercase tracking-wide text-[#64748B]">Skipped</div>
          </div>
          <div className="rounded-md border border-[#E5E7EB] bg-[#F8FAFC] py-2.5">
            <div data-testid="resnapshot-failed" className="text-lg font-semibold text-red-500">
              {failed}
            </div>
            <div className="text-[11px] uppercase tracking-wide text-[#64748B]">Failed</div>
          </div>
        </div>

        {/* Fatal error banner (e.g. browser engine unavailable) */}
        {fatalError && (
          <div
            data-testid="resnapshot-fatal-error"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
          >
            <span className="font-medium">Fatal:</span> {fatalError}
          </div>
        )}

        {/* Errors collapsible-style list — only show on terminal state
            to avoid flicker during live updates. Capped to 5 for
            density; full list is in the backend response. */}
        {terminal && errors.length > 0 && (
          <div className="mt-3 max-h-40 overflow-y-auto rounded-md border border-[#FEE2E2] bg-red-50/40 p-2 text-[11px] text-red-800">
            <div className="font-semibold mb-1">
              {errors.length} step{errors.length === 1 ? "" : "s"} reported issues:
            </div>
            <ul className="space-y-1" data-testid="resnapshot-errors-list">
              {errors.slice(0, 5).map((err, i) => (
                <li key={i} className="font-mono leading-snug">
                  <span className="text-red-500 mr-1">#{(err.step_index ?? 0) + 1}</span>
                  <span className="text-red-700/70 mr-1">[{err.phase || "exec"}]</span>
                  <span>{(err.error || "").slice(0, 120)}</span>
                </li>
              ))}
              {errors.length > 5 && (
                <li className="text-red-700/70 italic">
                  …and {errors.length - 5} more.
                </li>
              )}
            </ul>
          </div>
        )}

        <DialogFooter className="mt-4">
          <button
            type="button"
            disabled={!terminal}
            onClick={() => onClose?.()}
            data-testid="resnapshot-close-button"
            className="px-4 h-9 text-sm font-medium border border-[#E5E7EB] bg-white text-[#0F172A] rounded-md hover:bg-[#F8FAFC] hover:border-[#CBD5E1] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {terminal ? "Close" : "Running…"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
